import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_summary-hub-95/artifacts/v1xz43s3_Gasman%20Logo.png";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-gray-600 leading-relaxed space-y-3">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <img src={LOGO_URL} alt="GasMan4U" className="h-8 w-8 object-contain" />
          <span className="font-bold text-gray-900">GasMan4U</span>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Title */}
        <div className="flex items-start gap-4 mb-10">
          <div className="p-3 bg-[#e8eef9] rounded-xl mt-1">
            <Shield size={24} className="text-[#0A4CAE]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-500 mt-1 text-sm">
              Last updated: July 2025
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 sm:px-10 py-8">

          <p className="text-gray-600 leading-relaxed mb-8">
            Welcome to <strong>GasMan4U</strong> ("we", "our", or "us"). We are
            committed to protecting the personal information you share with us
            when you use our app or website to order gas cylinders, water cans,
            regulators, and related products. This Privacy Policy explains what
            data we collect, how we use it, and the choices you have.
          </p>

          <Section title="1. Information We Collect">
            <p>When you use GasMan4U, we collect the following information:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Contact details</strong> — your mobile number (used for
                OTP verification), name, and email address (if provided).
              </li>
              <li>
                <strong>Delivery addresses</strong> — the addresses you save in
                your account for doorstep delivery.
              </li>
              <li>
                <strong>Order information</strong> — products ordered, quantities,
                prices, payment method selected, and order history.
              </li>
              <li>
                <strong>Device & usage data</strong> — device type, operating
                system, and basic app usage patterns to help us improve the
                experience.
              </li>
            </ul>
            <p>
              We do <strong>not</strong> collect payment card numbers or banking
              credentials. Payments are handled via your chosen payment method
              (cash on delivery or third-party payment platforms).
            </p>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use your information solely to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Verify your identity via OTP and create your account.</li>
              <li>
                Process, confirm, and deliver your orders to your saved address.
              </li>
              <li>
                Communicate order updates, delivery status, and support messages
                via SMS, WhatsApp, or phone call.
              </li>
              <li>
                Personalise your experience (e.g., showing recently ordered
                products and frequently bought items).
              </li>
              <li>
                Improve our app, resolve issues, and prevent fraud or misuse.
              </li>
            </ul>
            <p>We do not use your data for advertising or sell it to any third party.</p>
          </Section>

          <Section title="3. Sharing Your Information">
            <p>
              We share your information only when necessary to fulfil your order:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Local distributors & delivery partners</strong> — your
                name, phone number, and delivery address are shared with the
                distributor assigned to your area so they can complete your
                delivery.
              </li>
              <li>
                <strong>OTP service provider (MSG91)</strong> — your mobile number
                is shared with MSG91 solely to send you a one-time password for
                login. MSG91 is bound by its own privacy and security standards.
              </li>
            </ul>
            <p>
              We do not sell, rent, or trade your personal information to any
              other third parties.
            </p>
          </Section>

          <Section title="4. Data Retention">
            <p>
              We retain your personal information for as long as your account is
              active or as needed to provide our services. If you request account
              deletion, your account will be marked for deletion immediately and
              permanently deleted on the seventh day, except
              where we are required by law to retain certain records (e.g., for
              accounting or dispute resolution).
            </p>
          </Section>

          <Section title="5. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Access</strong> the personal information we hold about you.
              </li>
              <li>
                <strong>Correct</strong> any inaccurate data in your profile.
              </li>
              <li>
                <strong>Delete</strong> your account and associated data by
                contacting us.
              </li>
              <li>
                <strong>Opt out</strong> of non-essential communications at any
                time.
              </li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the details
              in Section 9.
            </p>
          </Section>

          <Section title="6. Data Deletion">
            <p>
              You have the right to request the complete deletion of your personal
              data from our platform at any time. This includes your account
              information, saved addresses, order history, and any other data we
              hold about you.
            </p>
            <p>
              When you request deletion, your account is marked for deletion
              immediately. You may restore your account at any time during the
              following seven-day deletion window. On the seventh day, our
              scheduled deletion process will permanently delete the account and
              its associated personal data.
            </p>
            <p>
              Once deleted, your data cannot be recovered. If you wish to use
              GasMan4U again after deletion, you will need to create a new account.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We take reasonable technical and organisational measures to protect
              your personal information against unauthorised access, loss, or
              misuse. Access to your account is secured using OTP-based
              authentication, and all data is transmitted over HTTPS. However, no
              method of transmission over the internet is 100% secure, and we
              cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              GasMan4U is not directed at children under the age of 13. We do not
              knowingly collect personal information from children. If you believe
              a child has provided us with their information, please contact us
              immediately and we will delete it.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or legal requirements. When we make
              changes, we will update the "Last updated" date at the top of this
              page. We encourage you to review this policy periodically. Continued
              use of the app after any changes constitutes your acceptance of the
              updated policy.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              If you have any questions, concerns, or requests regarding this
              Privacy Policy or your personal data, please reach out to us:
            </p>
            <div className="mt-3 bg-[#eef2fb] rounded-xl p-4 border border-[#b3c4e8]">
              <p className="font-semibold text-gray-800">GasMan4U</p>
              <p className="text-gray-600 mt-1">
                The Local Delivery Solution
              </p>
              <p className="text-[#0A4CAE] mt-1 font-medium">
                Available via the app's calling and WhatsApp support numbers.
              </p>
            </div>
          </Section>

        </div>

        <p className="text-center text-gray-400 text-sm mt-8">
          © {new Date().getFullYear()} GasMan4U. All rights reserved.
        </p>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
